/**
 * Реестр запущенных выгрузок карточек.
 *
 * Окно статуса закрывается, а операция продолжается на сервере — поэтому состав
 * выгрузки и накопленные итоги живут не в стейте окна, а здесь, и переживают
 * перезагрузку страницы. Благодаря этому окно открывается заново из списка
 * фоновых задач с тем же содержимым.
 */

import { create } from 'zustand'
import { dedupeResults } from './publishScope'
import type { PublishCardResult, PublishJobRecord, PublishJobSnapshot } from './types'

const LS_KEY = 'marketplacePublish.jobs'
/** Больше не храним: реестр нужен для «вернуться к недавней выгрузке», не для архива */
const MAX_RECORDS = 20

function loadRecords(): Record<string, PublishJobRecord> {
  try {
    const raw = localStorage.getItem(LS_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {}
    return parsed as Record<string, PublishJobRecord>
  } catch {
    return {}
  }
}

function persist(records: Record<string, PublishJobRecord>): void {
  try {
    const trimmed = Object.values(records)
      .sort((a, b) => String(b.startedAt).localeCompare(String(a.startedAt)))
      .slice(0, MAX_RECORDS)
    localStorage.setItem(
      LS_KEY,
      JSON.stringify(Object.fromEntries(trimmed.map((record) => [record.jobId, record]))),
    )
  } catch {
    // квота или приватный режим — реестр остаётся только в памяти
  }
}

/**
 * Запись под задачу, о запуске которой этот браузер ничего не знает.
 *
 * Так бывает штатно: выгрузку запустили в другой вкладке или на другой машине,
 * localStorage почистили, открыли окно из списка фоновых задач. Раньше итоги
 * такой задачи молча выбрасывались (сливать было некуда) — и окно показывало
 * пустоту при том, что сервер прислал полный расклад по карточкам.
 */
function restoredRecord(jobId: string): PublishJobRecord {
  return {
    jobId,
    scope: 'all',
    startedAt: new Date().toISOString(),
    articles: [],
    targets: [],
    results: [],
    restored: true,
  }
}

interface PublishJobsState {
  records: Record<string, PublishJobRecord>
  /** jobId открытого окна статуса; null — окно закрыто */
  openJobId: string | null
  registerJob: (record: PublishJobRecord) => void
  mergeResults: (jobId: string, results: PublishCardResult[]) => void
  /** Свежий retry одной строки: результат и сводка пишутся атомарно. */
  applyMergeRetry: (jobId: string, result: Pick<
    PublishCardResult,
    'card_id' | 'merge_label' | 'imt_id' | 'merge_status'
  >) => void
  /** Последнее известное состояние задачи: переживает уборку виджета фоновых задач */
  mergeSnapshot: (jobId: string, snapshot: PublishJobSnapshot) => void
  openJob: (jobId: string) => void
  closeJob: () => void
  reset: () => void
}

export const usePublishJobsStore = create<PublishJobsState>((set, get) => ({
  records: loadRecords(),
  openJobId: null,

  registerJob: (record) => {
    set((state) => {
      const records = { ...state.records, [record.jobId]: record }
      persist(records)
      return { records }
    })
  },

  mergeResults: (jobId, results) => {
    if (!jobId || !results.length) return
    const current = get().records[jobId] ?? restoredRecord(jobId)
    const retryIds = new Set(current.mergeRetryCardIds ?? [])
    const currentByCardId = new Map(current.results
      .filter((result) => result.card_id !== null)
      .map((result) => [result.card_id!, result]))
    const guardedResults = results.map((result) => {
      const protectedResult = result.card_id === null ? undefined : currentByCardId.get(result.card_id)
      if (!protectedResult || !retryIds.has(result.card_id!)) return result
      return {
        ...result,
        merge_label: protectedResult.merge_label,
        imt_id: protectedResult.imt_id,
        merge_status: protectedResult.merge_status,
      }
    })
    const merged = dedupeResults([...current.results, ...guardedResults])
    if (
      get().records[jobId] &&
      merged.length === current.results.length &&
      JSON.stringify(merged) === JSON.stringify(current.results)
    ) {
      return
    }
    set((state) => {
      const records = { ...state.records, [jobId]: { ...current, results: merged } }
      persist(records)
      return { records }
    })
  },

  applyMergeRetry: (jobId, retryResult) => {
    const current = get().records[jobId]
    const cardId = retryResult.card_id
    if (!current || cardId === null || !Number.isSafeInteger(cardId) || cardId <= 0) return
    let changed = false
    const results = current.results.map((result) => {
      if (result.card_id !== cardId) return result
      changed = true
      return { ...result, ...retryResult }
    })
    if (!changed) return
    const mergedResults = results.filter((result) => result.merge_status === 'merged')
    const bundles = {
      created: new Set(mergedResults
        .map((result) => result.imt_id)
        .filter((imtId): imtId is number => imtId !== null)).size,
      topped_up: mergedResults.length,
      failed_cards: results.filter((result) => result.merge_status === 'failed').length,
    }
    const job = current.job
      ? {
        ...current.job,
        bundles,
      }
      : current.job
    const mergeRetryCardIds = [...new Set([...(current.mergeRetryCardIds ?? []), cardId])]
    set((state) => {
      const records = {
        ...state.records,
        [jobId]: { ...current, results, job, mergeRetryCardIds },
      }
      persist(records)
      return { records }
    })
  },

  mergeSnapshot: (jobId, snapshot) => {
    if (!jobId || !snapshot) return
    const current = get().records[jobId] ?? restoredRecord(jobId)
    const nextSnapshot = current.mergeRetryCardIds?.length && current.job?.bundles
      ? { ...snapshot, bundles: current.job.bundles }
      : snapshot
    const previous = current.job
    // Дребезг снимков — обычное дело: событие и ответ на запрос состояния
    // приходят вперемешку. Записываем только содержательное изменение, иначе
    // каждый заход опроса писал бы в localStorage новый seenAt.
    if (
      previous &&
      previous.status === nextSnapshot.status &&
      previous.currentStep === nextSnapshot.currentStep &&
      previous.percentage === nextSnapshot.percentage &&
      previous.error === nextSnapshot.error &&
      JSON.stringify(previous.catalogFreshness ?? []) === JSON.stringify(nextSnapshot.catalogFreshness ?? []) &&
      previous.catalogFreshnessEnforced === nextSnapshot.catalogFreshnessEnforced &&
      JSON.stringify(previous.bundles ?? null) === JSON.stringify(nextSnapshot.bundles ?? null) &&
      get().records[jobId]
    ) {
      return
    }
    set((state) => {
      const records = { ...state.records, [jobId]: { ...current, job: nextSnapshot } }
      persist(records)
      return { records }
    })
  },

  openJob: (jobId) => set({ openJobId: jobId }),
  closeJob: () => set({ openJobId: null }),

  reset: () => {
    try {
      localStorage.removeItem(LS_KEY)
    } catch {
      // ничего: реестр всё равно сбрасывается в памяти
    }
    set({ records: {}, openJobId: null })
  },
}))

export function getPublishRecord(jobId: string | null | undefined): PublishJobRecord | null {
  if (!jobId) return null
  return usePublishJobsStore.getState().records[jobId] ?? null
}

/** Тип фоновой задачи выгрузки — дословно тот, что заводит wildberries-service */
export const PUBLISH_CARDS_JOB_TYPE = 'marketplace_card_publish'

/**
 * Наша ли это задача. Реестр — источник правды; имя типа задачи учитываем как
 * запасной признак: запись могла не сохраниться (приватный режим, другой
 * браузер), а вернуться к выгрузке оператору всё равно нужно.
 */
export function isPublishCardsJob(job: { jobId?: string; type?: string } | null): boolean {
  if (!job?.jobId) return false
  if (String(job.type ?? '') === PUBLISH_CARDS_JOB_TYPE) return true
  if (usePublishJobsStore.getState().records[job.jobId]) return true
  return /publish/i.test(String(job.type ?? ''))
}
